import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { certificateAPI } from '../api/certificateAPI';
import EduSiteHeader from '../../edu-landing/EduSiteHeader';
import { withAppBasename } from '../../../config/appBase';
import EduSiteFooter from '../../edu-landing/EduSiteFooter';
import './CertificateVerification.css';

// Official Serrated Rosette Starburst Seal Mark
function ScallopedSeal() {
  return (
    <div className="ivori-cert-rosette-wrap" title="Official IVORI For Education Verified Seal">
      <svg className="ivori-cert-rosette-svg" viewBox="0 0 96 96" fill="none">
        <defs>
          <radialGradient id="sealGoldGrad" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="var(--iv-accent)" />
            <stop offset="45%" stopColor="var(--iv-accent)" />
            <stop offset="100%" stopColor="var(--iv-accent)" />
          </radialGradient>
          <filter id="sealDropShadow" x="-10%" y="-10%" width="130%" height="130%">
            <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" floodColor="#1F2533" floodOpacity="0.22" />
          </filter>
        </defs>
        {/* 24-point Starburst Rosette */}
        <polygon
          points="48.0,4.0 53.16,8.84 59.39,5.5 63.12,11.51 70.0,9.89 72.05,16.66 79.11,16.89 79.34,23.95 86.11,26.0 84.49,32.88 90.5,36.61 87.16,42.84 92.0,48.0 87.16,53.16 90.5,59.39 84.49,63.12 86.11,70.0 79.34,72.05 79.11,79.11 72.05,79.34 70.0,86.11 63.12,84.49 59.39,90.5 53.16,87.16 48.0,92.0 42.84,87.16 36.61,90.5 32.88,84.49 26.0,86.11 23.95,79.34 16.89,79.11 16.66,72.05 9.89,70.0 11.51,63.12 5.5,59.39 8.84,53.16 4.0,48.0 8.84,42.84 5.5,36.61 11.51,32.88 9.89,26.0 16.66,23.95 16.89,16.89 23.95,16.66 26.0,9.89 32.88,11.51 36.61,5.5 42.84,8.84"
          fill="url(#sealGoldGrad)"
          stroke="var(--iv-on-band)"
          strokeWidth="1.2"
          filter="url(#sealDropShadow)"
        />
        {/* Outer Circular Inset Ring */}
        <circle cx="48" cy="48" r="34" stroke="var(--iv-on-band)" strokeWidth="1" strokeDasharray="2 1.5" fill="none" opacity="0.85" />
        {/* Inner Solid Circular Ring */}
        <circle cx="48" cy="48" r="30" stroke="var(--iv-on-band)" strokeWidth="0.8" fill="none" opacity="0.6" />
        {/* Center IVORI Logo Emblem (No Text) */}
        <image
          href="/logo-yellow.png"
          x="23"
          y="23"
          width="50"
          height="50"
          preserveAspectRatio="xMidYMid meet"
        />
      </svg>
    </div>
  );
}

export default function CertificateVerification(props) {
  const { certId: paramId } = useParams();
  const certId = props.certId || paramId || 'IVORI-MEM-DEFAULT';

  const [copied, setCopied] = useState(false);
  const [postCopied, setPostCopied] = useState(false);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);

  const [certData, setCertData] = useState({
    id: certId,
    studentName: 'IVORI Member',
    programme: 'IVORI Verified Student Member',
    issueDate: 'October 2026',
    issueYear: '2026',
    issueMonth: '10',
    duration: 'Continuous Membership',
    status: 'VALID',
    issuer: 'IVORI Edu',
    location: 'London & Oxford, UK',
    description: 'holds verified student membership in the IVORI For Education academic learning and admissions intelligence ecosystem.',
    skills: ['Critical Decision Modeling', 'Admissions Intelligence', 'Continuous Learning']
  });

  useEffect(() => {
    let isMounted = true;

    certificateAPI.getCertificate(certId)
      .then((data) => {
        if (isMounted && data) {
          let prog = data.programme_name || 'IVORI Verified Student Member';
          let desc = data.description || '';
          let issueDate = data.issue_date || 'October 2026';
          let issueYear = data.issue_year || '2026';
          let issueMonth = data.issue_month || '10';
          let duration = data.duration || 'Continuous Membership';

          if (
            prog.includes('Admissions & Decision Modeling') ||
            (data.id && data.id.toUpperCase().startsWith('IVORI-EXP') && !prog.includes('Accounting') && !prog.includes('Machine Learning'))
          ) {
            prog = 'Experience Week: Investment Banking & Financial Markets (Loizou & Co)';
            desc = 'has successfully completed in July 2026 the IVORI For Education Work Experience Programme in Investment Banking, M&A Case Modeling, and Capital Markets Strategy, delivered in partnership with Loizou & Co.';
            issueDate = 'July 2026';
            issueYear = '2026';
            issueMonth = '07';
            duration = 'Experience Week Intensive';
          } else if (data.id && data.id.toUpperCase().startsWith('IVORI-CREATOR')) {
            prog = 'IVORI Certified Creator';
            desc = 'holds verified creator recognition for interactive application and decision simulation development in IVORI For Education.';
            duration = 'Continuous Recognition';
          } else if (!desc || desc.startsWith('Official verified membership') || desc.startsWith('Official verified completion')) {
            desc = 'holds verified student membership in the IVORI For Education academic learning and admissions intelligence ecosystem.';
          } else {
            desc = desc.replace(/ivori for education/gi, 'IVORI For Education');
          }

          setCertData({
            id: data.id || certId,
            studentName: data.student_name || 'IVORI Member',
            programme: prog,
            issueDate: issueDate,
            issueYear: issueYear,
            issueMonth: issueMonth,
            duration: duration,
            status: data.status || 'VALID',
            issuer: data.issuer || 'IVORI Edu',
            location: data.location || 'London & Oxford, UK',
            description: desc,
            skills: Array.isArray(data.skills) ? data.skills : (typeof data.skills === 'string' ? JSON.parse(data.skills || '[]') : ['Financial Modeling & DCF', 'M&A Case Simulation', 'LBO Fundamentals', 'Capital Markets Strategy']),
            linkedinUrl: data.linkedin_url
          });
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.warn('Certificate fetch note:', err);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [certId]);

  // Handle auto-print if ?print=true is present
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('print=true')) {
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCopyLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : `https://ivoriai.com/edu/verify/${certData.id}`;
  
  const captionPresets = [
    `Check out what I have achieved with ivori for education! 🎓 Excited to share that I have earned my verified credential in "${certData.programme}".\n\nVerify credential: ${currentUrl}`,
    `Check out my recent achievements! Pleased to announce my verified completion of "${certData.programme}" with ivori for education.\n\nCredential ID: ${certData.id}\nVerification Link: ${currentUrl}`,
    `Excited to share a milestone in my academic & career journey with ivori for education! 🚀 Official Certification in "${certData.programme}".\n\nVerify: ${currentUrl}`
  ];

  const [selectedCaptionIndex, setSelectedCaptionIndex] = useState(0);
  const [customCaption, setCustomCaption] = useState('');

  useEffect(() => {
    setCustomCaption(captionPresets[selectedCaptionIndex] || captionPresets[0]);
  }, [selectedCaptionIndex, certData.programme, certData.id, currentUrl]);

  // Update dynamic document title & OpenGraph metadata
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = `${certData.studentName} — ${certData.programme} | ivori for education`;
    }
  }, [certData.studentName, certData.programme]);

  const linkedInAddUrl = certData.linkedinUrl || `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(certData.programme)}&organizationName=${encodeURIComponent('IVORI Edu')}&issueYear=${certData.issueYear}&issueMonth=${certData.issueMonth}&certUrl=${encodeURIComponent(currentUrl)}&certId=${certData.id}`;
  const linkedInShareFeedUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;

  const handleCopyAndOpenLinkedIn = () => {
    const textToCopy = customCaption || captionPresets[selectedCaptionIndex];
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      setPostCopied(true);
      setTimeout(() => setPostCopied(false), 3000);
    }
    window.open(linkedInShareFeedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopySharePost = () => {
    const textToCopy = customCaption || captionPresets[selectedCaptionIndex];
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
      setPostCopied(true);
      setTimeout(() => setPostCopied(false), 2500);
    }
  };

  const [lookupInput, setLookupInput] = useState('');
  const [lookupError, setLookupError] = useState('');

  const isLookupMode = !paramId || paramId === 'search' || paramId === 'lookup' || paramId === 'registry';

  const handleLookupSubmit = (e) => {
    e.preventDefault();
    const cleanId = lookupInput.trim().toUpperCase();
    if (!cleanId) {
      setLookupError('Please enter a valid Credential ID (e.g. IVORI-MEM-26039307)');
      return;
    }
    window.location.href = withAppBasename(`/verify/${cleanId}`);
  };

  // Structured Data (JSON-LD) for LLMs, Google, and Educational Verification
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOccupationalCredential",
    "name": certData.programme,
    "description": certData.description,
    "credentialCategory": "Membership",
    "recognizedBy": {
      "@type": "Organization",
      "name": "IVORI Edu",
      "url": "https://ivoriai.com",
      "sameAs": [
        "https://www.linkedin.com/company/ivori-ai",
        "https://ivoriai.com/"
      ]
    },
    "identifier": certData.id,
    "validIn": {
      "@type": "Country",
      "name": "United Kingdom"
    }
  };

  return (
    <div className="ivori-verification-site-wrapper">
      {/* JSON-LD Schema for AI Web Crawlers & Indexers */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />

      {/* Official ivoriai.com/edu / ivori for education Navigation Header */}
      <div className="ivori-cert-no-print">
        <EduSiteHeader />
      </div>

      {/* Lookup Mode (when visiting /verify with no specific certificate ID) */}
      {isLookupMode ? (
        <main className="ivori-cert-page ivori-cert-lookup-page">
          <div className="ivori-cert-grain" />

          <div className="ivori-lookup-container">
            <div className="ivori-lookup-hero">
              <div className="ivori-lookup-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>Institutional Credential Registry</span>
              </div>
              <h1 className="ivori-lookup-title">Verify Official Credentials & Certificates</h1>
              <p className="ivori-lookup-subtitle">
                Enter an official IVORI Credential ID to verify candidate authenticity, institutional honours, and programme completion records in real time.
              </p>

              {/* Search Form */}
              <form onSubmit={handleLookupSubmit} className="ivori-lookup-form">
                <div className="ivori-lookup-input-wrap">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    type="text"
                    placeholder="Enter Credential ID (e.g. IVORI-MEM-26039307)..."
                    value={lookupInput}
                    onChange={(e) => {
                      setLookupInput(e.target.value);
                      if (lookupError) setLookupError('');
                    }}
                    className="ivori-lookup-input"
                  />
                </div>
                <button type="submit" className="ivori-lookup-btn">
                  Verify Credential
                </button>
              </form>
              {lookupError && <div className="ivori-lookup-error">{lookupError}</div>}
            </div>

            {/* Free Verified Credentials Directory for Students & CVs */}
            <div className="ivori-free-creds-section">
              <div className="ivori-free-creds-header">
                <h2>Official IVORI Credentials for Your CV & LinkedIn</h2>
                <p>All active students and researchers can earn, verify, and export institutional credentials directly.</p>
              </div>

              <div className="ivori-free-creds-grid">
                {/* Credential 1 */}
                <div className="ivori-free-cred-card">
                  <div className="ivori-free-cred-badge-row">
                    <span className="ivori-free-cred-pill free">Free for Active Students</span>
                    <span className="ivori-free-cred-code">IVORI-MEM</span>
                  </div>
                  <h3>IVORI Verified Student Member</h3>
                  <p>
                    Confirms verified academic enrollment and active participation in admissions intelligence and cognitive decision modeling.
                  </p>
                  <div className="ivori-free-cred-skills">
                    <span>Critical Thinking</span>
                    <span>Admissions Intelligence</span>
                    <span>Decision Modeling</span>
                  </div>
                  <div className="ivori-free-cred-footer">
                    <a href={withAppBasename('/profile?section=certificates')} className="ivori-free-cred-link">
                      Claim in Profile Settings →
                    </a>
                  </div>
                </div>

                {/* Credential 2 */}
                <div className="ivori-free-cred-card">
                  <div className="ivori-free-cred-badge-row">
                    <span className="ivori-free-cred-pill clinical">Observational WEX</span>
                    <span className="ivori-free-cred-code">IVORI-WEX</span>
                  </div>
                  <h3>Virtual Clinical & Legal Work Experience</h3>
                  <p>
                    Verified completion of observational simulations, medical ethics cases, or high-court legal arguments.
                  </p>
                  <div className="ivori-free-cred-skills">
                    <span>Clinical Reasoning</span>
                    <span>Ethical Decision Making</span>
                    <span>Case Analysis</span>
                  </div>
                  <div className="ivori-free-cred-footer">
                    <a href={withAppBasename('/apps')} className="ivori-free-cred-link">
                      Explore Case Studies →
                    </a>
                  </div>
                </div>

                {/* Credential 3 */}
                <div className="ivori-free-cred-card">
                  <div className="ivori-free-cred-badge-row">
                    <span className="ivori-free-cred-pill honors">Research & Dev</span>
                    <span className="ivori-free-cred-code">IVORI-DEV</span>
                  </div>
                  <h3>Simulation & App Creator Honors</h3>
                  <p>
                    Awarded to students and researchers who design, publish, or contribute educational problem sets to the store.
                  </p>
                  <div className="ivori-free-cred-skills">
                    <span>Software Design</span>
                    <span>Problem Architecture</span>
                    <span>Peer Review</span>
                  </div>
                  <div className="ivori-free-cred-footer">
                    <a href={withAppBasename('/apps')} className="ivori-free-cred-link">
                      Build an App →
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Institutional Security Highlights */}
            <div className="ivori-lookup-trust-row">
              <div className="ivori-lookup-trust-item">
                <div className="ivori-lookup-trust-icon">✓</div>
                <div>
                  <strong>Cryptographically Backed</strong>
                  <p>Every certificate is recorded with an immutable serial ID and timestamp.</p>
                </div>
              </div>
              <div className="ivori-lookup-trust-item">
                <div className="ivori-lookup-trust-icon">in</div>
                <div>
                  <strong>Official LinkedIn Integration</strong>
                  <p>One-click verifiable licence addition under issuer name <em>IVORI Edu</em>.</p>
                </div>
              </div>
              <div className="ivori-lookup-trust-item">
                <div className="ivori-lookup-trust-icon">🏛</div>
                <div>
                  <strong>Institutional Recognition</strong>
                  <p>Standardized for university admissions, UCAS personal statements, and CVs.</p>
                </div>
              </div>
            </div>
          </div>
        </main>
      ) : (
        /* Certificate Canvas Mode (when a certId is provided) */
        <div className="ivori-cert-page">
        <div className="ivori-cert-grain" />

        {/* Certificate Action Topbar (Width Matched to Certificate Canvas) */}
        <div className="ivori-cert-topbar ivori-cert-no-print">
          <div className="ivori-cert-topbar-inner">
            <div className="ivori-cert-brand-wrap">
              <span className="ivori-cert-verified-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                Verified Credential Record
              </span>
            </div>

            <div className="ivori-cert-actions">
              <button
                type="button"
                className="ivori-cert-btn ivori-cert-btn-secondary"
                onClick={handleCopyLink}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
              </button>

              {/* Real LinkedIn Official Action Button & Modal Trigger */}
              <button
                type="button"
                className="ivori-cert-btn ivori-cert-btn-linkedin"
                onClick={() => setShowLinkedInModal(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                <span>Add / Share on LinkedIn (Issuer: IVORI Edu)</span>
              </button>

              <button
                type="button"
                className="ivori-cert-btn ivori-cert-btn-print"
                onClick={() => window.print()}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
                <span>Download PDF / Print</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Certificate Canvas */}
        <div className="ivori-cert-container">
          <div className="ivori-cert-frame">
            <div className="ivori-cert-inner-frame">
              {/* Ornate Corner Accents */}
              <div className="ivori-cert-corner ivori-cert-corner-tl" />
              <div className="ivori-cert-corner ivori-cert-corner-tr" />
              <div className="ivori-cert-corner ivori-cert-corner-bl" />
              <div className="ivori-cert-corner ivori-cert-corner-br" />

              {/* Institution Header */}
              <div className="ivori-cert-header">
                <div className="ivori-cert-inst-name">ivori for education</div>
                {((certData.description && certData.description.toLowerCase().includes('loizou')) ||
                  (certData.programme && (
                    certData.programme.toLowerCase().includes('investment banking') ||
                    certData.programme.toLowerCase().includes('accounting') ||
                    certData.programme.toLowerCase().includes('finance') ||
                    certData.programme.toLowerCase().includes('valuation')
                  ))) ? (
                  <div className="ivori-cert-partner-header">
                    <span className="ivori-cert-partner-label">In partnership with</span>
                    <img src="/loizou-and-co-logo.png" alt="Loizou & Co" className="ivori-cert-partner-logo" />
                  </div>
                ) : ((certData.description && (certData.description.toLowerCase().includes('ivori lab') || certData.description.toLowerCase().includes('lab'))) ||
                     (certData.programme && (
                       certData.programme.toLowerCase().includes('machine learning') ||
                       certData.programme.toLowerCase().includes('applied ai') ||
                       certData.programme.toLowerCase().includes('artificial intelligence') ||
                       certData.programme.toLowerCase().includes('computer science') ||
                       certData.programme.toLowerCase().includes('data science')
                     ))) ? (
                  <div className="ivori-cert-partner-header">
                    <span className="ivori-cert-partner-label">In partnership with</span>
                    <span className="ivori-cert-partner-lab-badge">
                      <img src="/logo-yellow.png" alt="IVORI Lab" className="ivori-cert-partner-lab-icon" />
                      <span className="ivori-cert-partner-lab-text">IVORI Lab</span>
                    </span>
                  </div>
                ) : null}
                <div className="ivori-cert-divider" />
              </div>

              {/* Certificate Body */}
              <div className="ivori-cert-body">
                <div className="ivori-cert-prefix">
                  {certData.programme && certData.programme.toLowerCase().includes('creator')
                    ? 'OFFICIAL CERTIFICATE OF RECOGNITION'
                    : certData.programme && (certData.programme.toLowerCase().includes('experience') || certData.programme.toLowerCase().includes('completion') || certData.programme.toLowerCase().includes('wex'))
                      ? 'OFFICIAL CERTIFICATE OF COMPLETION'
                      : 'OFFICIAL CERTIFICATE OF MEMBERSHIP'}
                </div>
                <h1 className="ivori-cert-title">{certData.programme}</h1>

                <div className="ivori-cert-attest">This is to certify that</div>

                <div className="ivori-cert-student-name-container">
                  <div
                    className="ivori-cert-student-name"
                    style={{
                      fontSize: certData.studentName && certData.studentName.length > 25
                        ? 'clamp(1.25rem, 2.8vw, 1.85rem)'
                        : certData.studentName && certData.studentName.length > 16
                          ? 'clamp(1.5rem, 3.4vw, 2.3rem)'
                          : 'clamp(1.85rem, 4vw, 2.85rem)'
                    }}
                  >
                    {certData.studentName}
                  </div>
                </div>

                <p className="ivori-cert-statement">
                  {certData.description}
                </p>
              </div>

              {/* Official Embossed Metallic Seal & Team Endorsement with Left/Right Flank Lines */}
              <div className="ivori-cert-footer">
                <div className="ivori-cert-seal-flank-line" />
                <div className="ivori-cert-seal-wrap">
                  <ScallopedSeal />
                  <div className="ivori-cert-team-signer">ivori for education team</div>
                </div>
                <div className="ivori-cert-seal-flank-line" />
              </div>

              {/* Security Ribbon / Meta */}
              <div className="ivori-cert-meta-ribbon">
                <div>
                  Credential ID: <span className="ivori-cert-meta-code">{certData.id}</span>
                </div>
                <div>
                  Issued: <span className="ivori-cert-meta-code">{certData.issueDate}</span>
                </div>
                <div>
                  Status: <span className="ivori-cert-meta-code" style={{ color: 'var(--iv-head)' }}>VERIFIED</span>
                </div>
                <div>
                  Direct Link: <span className="ivori-cert-meta-code">ivoriai.com/edu/verify/{certData.id}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* LinkedIn Share & Add to Profile Modal Widget */}
      {showLinkedInModal && (
        <div className="ivori-cert-modal-backdrop" onClick={() => setShowLinkedInModal(false)}>
          <div className="ivori-cert-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ivori-cert-modal-header">
              <div className="ivori-cert-modal-title-wrap">
                <div className="ivori-cert-modal-linkedin-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </div>
                <div>
                  <h3>LinkedIn Credential Integration</h3>
                  <p>Add to your professional profile or share to your feed</p>
                </div>
              </div>
              <button
                type="button"
                className="ivori-cert-modal-close"
                onClick={() => setShowLinkedInModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="ivori-cert-modal-body">
              <div className="ivori-cert-modal-card">
                <h4>Option 1: Add to LinkedIn Profile</h4>
                <p>Adds this credential directly into your LinkedIn <strong>Licenses & Certifications</strong> with official issuing authority <em>IVORI Edu</em>.</p>
                <a
                  href={linkedInAddUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ivori-cert-modal-btn ivori-cert-modal-btn-primary"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  <span>Add to Profile (Issuer: IVORI Edu)</span>
                </a>
              </div>

              <div className="ivori-cert-modal-card">
                <h4>Option 2: Share Accomplishment on Feed</h4>
                <p>Choose a predefined post caption to share your verified achievement:</p>

                <div className="ivori-cert-caption-presets">
                  <button
                    type="button"
                    className={`ivori-cert-caption-tab ${selectedCaptionIndex === 0 ? 'active' : ''}`}
                    onClick={() => setSelectedCaptionIndex(0)}
                  >
                    "Check what I achieved"
                  </button>
                  <button
                    type="button"
                    className={`ivori-cert-caption-tab ${selectedCaptionIndex === 1 ? 'active' : ''}`}
                    onClick={() => setSelectedCaptionIndex(1)}
                  >
                    "Recent achievements"
                  </button>
                  <button
                    type="button"
                    className={`ivori-cert-caption-tab ${selectedCaptionIndex === 2 ? 'active' : ''}`}
                    onClick={() => setSelectedCaptionIndex(2)}
                  >
                    "Milestone"
                  </button>
                </div>

                <textarea
                  className="ivori-cert-caption-preview"
                  value={customCaption}
                  onChange={(e) => setCustomCaption(e.target.value)}
                  rows={3}
                />

                <div className="ivori-cert-modal-share-actions">
                  <button
                    type="button"
                    className="ivori-cert-modal-btn ivori-cert-modal-btn-feed"
                    onClick={handleCopyAndOpenLinkedIn}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                    <span>{postCopied ? 'Caption Copied! Opening LinkedIn...' : 'Copy Caption & Open LinkedIn'}</span>
                  </button>
                  <button
                    type="button"
                    className="ivori-cert-modal-btn ivori-cert-modal-btn-copy"
                    onClick={handleCopySharePost}
                  >
                    {postCopied ? 'Copied!' : 'Copy Only'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Official ivoriai.com/edu / ivori for education Site Footer */}
      <div className="ivori-cert-no-print">
        <EduSiteFooter />
      </div>
    </div>
  );
}
